import React, { useState, useMemo } from 'react';
import {
    Box, Card, CardContent, Typography, Stack, TextField, Button, IconButton,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Chip, Alert, Tooltip, Divider
} from '@mui/material';
import {
    Add as AddIcon, Delete as DeleteIcon, Send as SendIcon,
    ContentCopy as CopyIcon, Visibility as ShowIcon
} from '@mui/icons-material';
import deviceConfigService from '../../../services/deviceConfigService';

/**
 * RAW-CAN filter configurator — builds the supplier's plain-text command:
 *   $CANCF,CanProtocol,RxIDCnt,Request,RemoteRequest,TxIdCnt,TxDLC,[RxIDs],[TxIDs]#
 * and publishes it to autopeepal/<imei>/command.
 *
 *   CanProtocol: 0=250k/11bit  1=250k/29bit  2=500k/11bit  3=500k/29bit
 *   Receive-all shortcut: $CANCF,0,0,0,0,0,0,[0]#
 *   With Tx IDs the DLC is included and a second [..] group is appended.
 */

const PROTOCOLS = [
    { value: '0', label: '250 kbps · 11-bit' },
    { value: '1', label: '250 kbps · 29-bit' },
    { value: '2', label: '500 kbps · 11-bit' },
    { value: '3', label: '500 kbps · 29-bit' },
];

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'monospace', fontWeight: 600 } };

// Keep only valid hex CAN-id characters; upper-case for readability.
const cleanId = (s) => String(s).replace(/[^0-9a-fA-F]/g, '').toUpperCase();

const IdList = ({ title, ids, setIds, help }) => (
    <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2" fontWeight={800}>{title} <Chip label={ids.filter(Boolean).length} size="small" sx={{ ml: 0.5, fontWeight: 700, height: 20 }} /></Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setIds([...ids, ''])}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}>Add ID</Button>
        </Stack>
        {help && <Typography variant="caption" color="text.secondary" display="block" mb={1}>{help}</Typography>}
        <Stack spacing={1}>
            {ids.length === 0 && <Typography variant="caption" color="text.disabled">No IDs — add one.</Typography>}
            {ids.map((id, i) => (
                <Stack direction="row" spacing={1} alignItems="center" key={i}>
                    <TextField
                        size="small" fullWidth placeholder="e.g. 302 or 181CFF15"
                        value={id}
                        onChange={e => setIds(ids.map((v, j) => j === i ? cleanId(e.target.value) : v))}
                        InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5, color: 'text.disabled', fontFamily: 'monospace' }}>0x</Typography> }}
                        sx={fieldSx}
                    />
                    <IconButton size="small" color="error" onClick={() => setIds(ids.filter((_, j) => j !== i))}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ))}
        </Stack>
    </Box>
);

const RawCanFilterBuilder = ({ deviceId, onSent, onNotify, disabled }) => {
    const [protocol, setProtocol] = useState('2');   // 500k/11-bit is the common default
    const [receiveAll, setReceiveAll] = useState(false);
    const [request, setRequest] = useState(false);
    const [remoteRequest, setRemoteRequest] = useState(false);
    const [txDlc, setTxDlc] = useState(8);
    const [rxIds, setRxIds] = useState(['302', '202']);
    const [txIds, setTxIds] = useState([]);
    const [sending, setSending] = useState(false);

    const rxClean = useMemo(() => rxIds.map(cleanId).filter(Boolean), [rxIds]);
    const txClean = useMemo(() => txIds.map(cleanId).filter(Boolean), [txIds]);

    const command = useMemo(() => {
        if (receiveAll) return '$CANCF,0,0,0,0,0,0,[0]#';
        const rxCnt = rxClean.length;
        const txCnt = txClean.length;
        const dlc = txCnt > 0 ? txDlc : 0;
        let s = `$CANCF,${protocol},${rxCnt},${request ? 1 : 0},${remoteRequest ? 1 : 0},${txCnt},${dlc},[${rxClean.join(',')}]`;
        if (txCnt > 0) s += `,[${txClean.join(',')}]`;
        return s + '#';
    }, [receiveAll, protocol, request, remoteRequest, txDlc, rxClean, txClean]);

    const valid = receiveAll || rxClean.length > 0;

    const send = async (cmd, category) => {
        try {
            setSending(true);
            const res = await deviceConfigService.sendCommand(deviceId, { command: cmd, category });
            onNotify?.(res.success ? `Sent: ${cmd}` : (res.message || 'Send failed'), res.success ? 'success' : 'error');
            if (res.success) onSent?.();
        } catch (err) {
            onNotify?.(err.response?.data?.message || err.message || 'Send failed', 'error');
        } finally { setSending(false); }
    };

    return (
        <Card sx={{ borderRadius: '20px', mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={800} mb={0.5}>🛰️ RAW CAN Filter</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Tell the TCU which CAN IDs to receive (and optionally transmit). The device must be in <strong>RAW CAN mode</strong> — use the <em>Commands</em> tab to switch it (SET CANM RAW → SAVE CONFIG → RESET) before applying a filter.
                </Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                    <FormControl size="small" sx={{ minWidth: 200 }} disabled={receiveAll}>
                        <InputLabel>CAN Protocol</InputLabel>
                        <Select value={protocol} label="CAN Protocol" onChange={e => setProtocol(e.target.value)} sx={{ borderRadius: '10px' }}>
                            {PROTOCOLS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={<Switch checked={receiveAll} onChange={e => setReceiveAll(e.target.checked)} />}
                        label={<Typography variant="body2" fontWeight={700}>Receive ALL IDs</Typography>}
                    />
                </Stack>

                {!receiveAll && (
                    <>
                        <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
                            <FormControlLabel
                                control={<Switch checked={request} onChange={e => setRequest(e.target.checked)} />}
                                label={<Typography variant="body2" fontWeight={700}>CAN Request</Typography>}
                            />
                            <FormControlLabel
                                control={<Switch checked={remoteRequest} onChange={e => setRemoteRequest(e.target.checked)} />}
                                label={<Typography variant="body2" fontWeight={700}>Remote Request</Typography>}
                            />
                            {txClean.length > 0 && (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Tx DLC</InputLabel>
                                    <Select value={txDlc} label="Tx DLC" onChange={e => setTxDlc(e.target.value)} sx={{ borderRadius: '10px' }}>
                                        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} mb={1}>
                            <Box flex={1}><IdList title="Receive IDs" ids={rxIds} setIds={setRxIds} help="CAN IDs the device should receive (hex, no 0x)." /></Box>
                            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                            <Box flex={1}><IdList title="Transmit IDs" ids={txIds} setIds={setTxIds} help="Optional — IDs the device should transmit (requests)." /></Box>
                        </Stack>
                    </>
                )}

                {/* Live command preview */}
                <Box sx={{ bgcolor: '#0f172a', borderRadius: '12px', p: 2, mt: 2, position: 'relative' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>Command to send</Typography>
                    <Tooltip title="Copy">
                        <IconButton size="small" onClick={() => { navigator.clipboard.writeText(command); onNotify?.('Command copied', 'info'); }}
                            sx={{ position: 'absolute', top: 6, right: 6, color: '#94a3b8' }}>
                            <CopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Typography sx={{ color: '#4ade80', fontFamily: 'monospace', fontWeight: 700, fontSize: '15px', mt: 0.5, wordBreak: 'break-all' }}>
                        {command}
                    </Typography>
                </Box>

                {!valid && <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>Add at least one Receive ID (or turn on “Receive ALL IDs”).</Alert>}

                <Stack direction="row" spacing={1.5} mt={2} flexWrap="wrap" useFlexGap>
                    <Button variant="contained" size="large" startIcon={<SendIcon />}
                        disabled={disabled || sending || !valid}
                        onClick={() => send(command, 'CAN_FILTER')}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, px: 4 }}>
                        {sending ? 'Sending…' : 'Apply Filter'}
                    </Button>
                    <Button variant="outlined" startIcon={<ShowIcon />}
                        disabled={disabled || sending}
                        onClick={() => send('SHOW CAN CFG', 'SHOW_CFG')}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}>
                        Show Current Config
                    </Button>
                </Stack>
                {disabled && <Typography variant="caption" color="error" display="block" mt={1}>Backend is not connected to the MQTT broker — sending is disabled.</Typography>}
            </CardContent>
        </Card>
    );
};

export default RawCanFilterBuilder;
